import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Checkbox, LoadError } from '../UI';
import { Button } from '../UI/base/Button';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import { useOpeningDaysEditor } from '../../hooks/useOpeningDaysEditor';
import { DAY_LABEL_KEYS, DAY_ORDER } from '../../constants/weekDayConstants';
import { OPENING_DAY_CHECKBOX_ID_PREFIX } from './openingDaysConstants';
import styles from './OpeningDaysSettings.module.css';

// The days of the week the location is usually open. Only the days: the hours
// of an open day follow from the staff working it and the bookings placed on it.
export const OpeningDaysSettings = () => {
  const { t } = useTranslation();
  const { locationId } = useParams<{ locationId: string }>();
  const { selectedDays, isLoading, hasLoadError, hasNoDays, canSave, toggleDay, retry, save } =
    useOpeningDaysEditor(locationId ?? null);

  const renderDays = (): ReactNode => {
    if (isLoading) return <p className={styles.emptyState}>{t('config-opening-days-loading')}</p>;
    if (hasLoadError) {
      return <LoadError message={t('config-opening-days-load-error')} onRetry={retry} />;
    }
    return (
      <fieldset className={styles.dayList} aria-label={t('config-opening-days-header')}>
        {DAY_ORDER.map((day) => (
          <Checkbox
            key={day}
            id={`${OPENING_DAY_CHECKBOX_ID_PREFIX}${day}`}
            isChecked={selectedDays.includes(day)}
            onChange={() => toggleDay(day)}
            label={t(DAY_LABEL_KEYS[day])}
          />
        ))}
      </fieldset>
    );
  };

  return (
    <section>
      <SectionHeader title={t('config-opening-days-header')}>
        <Button type="primary" disabled={!canSave} onClick={save}>
          {t('config-opening-days-save')}
        </Button>
      </SectionHeader>
      <p className={styles.hint}>{t('config-opening-days-hint')}</p>
      {renderDays()}
      {hasNoDays && <p className={styles.error}>{t('config-opening-days-error-none')}</p>}
    </section>
  );
};
